import React, { useState } from "react";
import { ORGANIZATIONS_PAGE_TITLE } from "./organizations.constants";
import { useOrganizations } from "./hooks";
import { EmptyState, OrganizationsTable } from "./components";
import LoadingDots from "@/shared/components/LoadingDots";
import { Alert } from "@/shared/components/ui/alert";
import AddOrganizationModal from "./components/AddOrganizationModal";
import EditOrganizationModal from "./components/EditOrganizationModal";
import {
  Organization,
  OrganizationService,
} from "@/shared/services/organization.service";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { toast } from "sonner";

/**
 * Organizations page component
 * Displays a list of all organizations
 */
const Organizations: React.FC = () => {
  const { organizations, loading, error, refetch } = useOrganizations();
  const [editingOrganization, setEditingOrganization] =
    useState<Organization | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [organizationToDelete, setOrganizationToDelete] =
    useState<Organization | null>(null);

  const handleEditOrganization = (organization: Organization) => {
    setEditingOrganization(organization);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingOrganization(null);
  };

  const handleDeleteOrganization = (organization: Organization) => {
    setOrganizationToDelete(organization);
    setDeleteDialogOpen(true);
  };

  const handleActivateOrganization = async (organization: Organization) => {
    try {
      await OrganizationService.activate(organization.id);
      toast.success(
        `Organization "${organization.name}" activated successfully`
      );
      refetch();
    } catch (error) {
      console.error("Failed to activate organization:", error);
      toast.error("Failed to activate organization");
    }
  };

  const confirmDelete = async () => {
    if (!organizationToDelete) return;

    try {
      await OrganizationService.delete(organizationToDelete.id);
      toast.success("Organization deleted successfully");
      setDeleteDialogOpen(false);
      setOrganizationToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete organization:", error);
      toast.error("Failed to delete organization");
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{ORGANIZATIONS_PAGE_TITLE}</h1>
        <AddOrganizationModal onOrganizationAdded={refetch} />
      </div>

      {loading ? (
        <LoadingDots />
      ) : error ? (
        <Alert variant="destructive">{error}</Alert>
      ) : organizations.length === 0 ? (
        <EmptyState />
      ) : (
        <OrganizationsTable
          organizations={organizations}
          onEditOrganization={handleEditOrganization}
          onDeleteOrganization={handleDeleteOrganization}
          onActivateOrganization={handleActivateOrganization}
        />
      )}

      <EditOrganizationModal
        organization={editingOrganization}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onOrganizationUpdated={refetch}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the organization "
              {organizationToDelete?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Organizations;
